import Store from "common/store";
import StoreKeys from "common/storeKeys";
import { SetLoading } from "common/components/Loader/Loader";
// eslint-disable-next-line no-unused-vars
import EventPicksDto from "common/types/EventPicksDto";
import BaseEvent from "./BaseEvent";
import PickService from "services/PickService";
// eslint-disable-next-line no-unused-vars
import { GroupStagePick } from "common/types/GroupStagePick";
import PicksHelper from "common/picksHelper";

export default class GenericEvent extends BaseEvent {
	async fullLoad(userId) {
		SetLoading(true);
		await Promise.all([
			this.userHasBracket(),
			this.loadUserScore(userId),
			this.loadLeaderboard(),
			this.loadOfficialPicks(),
			this.loadBracketUser(userId),
			this.loadChampionPick(userId),
		]);

		await this.loadEventPicks(userId);
		this.checkLoadFirstPlace(userId, this.fullLoad.bind(this));
	}

	async loadChampionPick(userId) {
		const format = Store.get(StoreKeys.EVENT.FORMAT);
		if (!format?.championPick?.enabled) {
			Store.set(StoreKeys.EVENT.CHAMPION_PICK, null);
			return;
		}

		const pick = await PickService.getChampionPick(
			this.eventData.id,
			userId
		).catch(() => null);
		Store.set(StoreKeys.EVENT.CHAMPION_PICK, pick || null);
	}

	async userHasBracket() {
		const eventId = this.eventData.id;
		await PickService.getUserPicks(eventId).then(
			/** @param {EventPicksDto} picks */
			(picks) => {
				const submittedStageIds = [
					...new Set(
						[...(picks.groupStagePicks ?? []), ...(picks.matchPicks ?? [])].map(
							(p) => p.eventStageId
						)
					),
				];
				Store.set(
					StoreKeys.EVENT.USER.SUBMITTED_STAGE_IDS,
					submittedStageIds
				);

				const eventStage = this.eventData.eventStages[this.eventData.stage];
				this.hasBracket = submittedStageIds.includes(eventStage?.id);

				const picksHelper = new PicksHelper(this.eventData);
				const isLocked = picksHelper.isLocked();

				Store.set(StoreKeys.EVENT.HAS_BRACKET, this.hasBracket);

				if (!isLocked && !this.hasBracket) {
					Store.set(StoreKeys.EVENT.IS_EDITING, true);
					Store.set(StoreKeys.EVENT.STEP, 0);
				}
			}
		);
	}

	async loadEventPicks(userId) {
		const eventId = this.eventData.id;
		await PickService.getUserPicks(eventId, userId).then(
			/** @param {EventPicksDto} picks */
			(picks) => {
				const groupStagePicks = picks.groupStagePicks ?? [];
				Store.set(StoreKeys.EVENT.USER.GROUP_PICKS, groupStagePicks);

				let bracketPicks = picks.matchPicks ?? [];
				Store.set(StoreKeys.EVENT.USER.BRACKET_PICKS, bracketPicks);

				Store.set(StoreKeys.EVENT.STAGE_INDEX, this.eventData.stage);
				Store.set(StoreKeys.EVENT.STEP, 0);
				Store.set(StoreKeys.EVENT.SUBSTAGE_INDEX, 0);
			}
		);
	}

	/** @returns {Promise<Array<number>>} the stages the picks were saved for. */
	async submitPicks() {
		// The server resolves teams from the ids.
		const withoutTeams = (pick) => ({
			...pick,
			topTeam: null,
			bottomTeam: null,
			winnerTeam: null,
			loserTeam: null,
		});

		const groupStagePicks = Store.get(StoreKeys.EVENT.USER.GROUP_PICKS) ?? [];
		// An unplayed match carries null team ids, which model binding rejects.
		const matchPicks = (Store.get(StoreKeys.EVENT.USER.BRACKET_PICKS) ?? [])
			.filter((p) => p.winnerTeamId && p.loserTeamId)
			.map(withoutTeams);

		await PickService.saveUserPicks(this.eventData.id, {
			groupStagePicks,
			matchPicks,
		});

		return [
			...new Set(
				[...groupStagePicks, ...matchPicks].map((p) => p.eventStageId)
			),
		];
	}
}
